import { useEffect, useState, type FormEvent } from 'react';
import { ApiError, api, type LinkInfo, type Me } from '../lib/api';

/**
 * Where an invitation or a password-reset email lands.
 *
 * Says who the link is for, asks for a password (and a name, for somebody
 * joining), and signs them straight in. A link that no longer works says why
 * and offers the way forward, rather than a bare error.
 */
export function LinkPage({ token, onSignedIn }: { token: string; onSignedIn: (me: Me) => void }) {
  const [info, setInfo] = useState<LinkInfo | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .inspectLink(token)
      .then((i) => {
        setInfo(i);
        setName(i.name ?? '');
      })
      .catch((err) => setProblem(err instanceof ApiError ? err.message : 'Could not reach the server. Try again.'));
  }, [token]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (busy) return;
    if (password.length < 10) {
      setError('Ten characters at least.');
      return;
    }
    if (password !== confirm) {
      setError('The two passwords do not match.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.completeLink({ token, password, name: info?.purpose === 'invite' ? name.trim() || undefined : undefined });
      /* The link is spent; leave nothing in the address bar that looks reusable. */
      window.history.replaceState(null, '', '/home');
      onSignedIn(await api.me());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not reach the server. Try again.');
      setBusy(false);
    }
  };

  const joining = info?.purpose === 'invite';

  return (
    <div className="auth-shell">
      <form className="auth-card" onSubmit={submit}>
        <div className="auth-brand">
          <img className="auth-wordmark" src="/brand/allrouted-wordmark.png" alt="AllRouted" width="466" height="72" />
        </div>

        {problem ? (
          <>
            <h1 className="auth-title">This link does not work</h1>
            <p className="auth-error" role="alert">
              {problem}
            </p>
            <p className="auth-alt">
              <a href="/home">Go to sign in</a>
            </p>
          </>
        ) : !info ? (
          <div className="auth-loading">Checking your link…</div>
        ) : (
          <>
            <h1 className="auth-title">{joining ? `Join ${info.accountName}` : 'Choose a new password'}</h1>
            <p className="verify-text">
              {joining ? (
                <>
                  You are setting up <strong>{info.email}</strong> on AllRouted. Choose a password and you are in.
                </>
              ) : (
                <>
                  For <strong>{info.email}</strong>. You will be signed out everywhere else.
                </>
              )}
            </p>

            {error ? (
              <p className="auth-error" role="alert">
                {error}
              </p>
            ) : null}

            <input type="email" autoComplete="username" value={info.email} readOnly hidden />
            {joining ? (
              <label className="auth-field">
                <span>Your name</span>
                <input value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" />
              </label>
            ) : null}
            <label className="auth-field">
              <span>{joining ? 'Password' : 'New password'}</span>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                minLength={10}
                required
                autoFocus={!joining}
              />
              <em className="auth-help">At least 10 characters.</em>
            </label>
            <label className="auth-field">
              <span>Repeat it</span>
              <input
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="new-password"
                required
              />
            </label>

            <button className="auth-submit" type="submit" disabled={busy}>
              {busy ? 'One moment…' : joining ? 'Join and sign in' : 'Save and sign in'}
            </button>
          </>
        )}
      </form>
    </div>
  );
}
